import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import { useLanguage } from '../contexts/LanguageContext';
import { Sparkles, Clock, Users, Trophy, ArrowRight, X } from 'lucide-react';

export const WELCOME_VERSION = '2024.12';

export const getWelcomeStorageKey = (userId?: string) =>
  `msd_welcome_${WELCOME_VERSION}_${userId ?? 'anon'}`;

interface OnboardingTourProps {
  onFinish: () => void;
}

export const OnboardingTour: React.FC<OnboardingTourProps> = ({ onFinish }) => {
  const { t, isRTL } = useLanguage();
  const [isClosing, setIsClosing] = useState(false);

  const persistSeen = useCallback(async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const storageKey = getWelcomeStorageKey(user?.id);
      localStorage.setItem(storageKey, '1');

      if (user) {
        await supabase.auth.updateUser({
          data: { onboarding_seen: true, welcome_version: WELCOME_VERSION },
        });
      }
    } catch (err) {
      console.warn('Onboarding persistence error:', err);
    }
  }, []);

  const finish = useCallback(async () => {
    if (isClosing) return;
    setIsClosing(true);
    await persistSeen();
    setTimeout(() => {
      onFinish();
    }, 220);
  }, [isClosing, onFinish, persistSeen]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === 'Enter') {
        event.preventDefault();
        finish();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [finish]);

  const features = [
    {
      icon: Sparkles,
      label: t.welcomeFeaturePlanLabel,
      detail: t.welcomeFeaturePlanDetail,
      glow: 'from-amber-400/50 via-orange-400/10 to-transparent',
    },
    {
      icon: Clock,
      label: t.welcomeFeatureFocusLabel,
      detail: t.welcomeFeatureFocusDetail,
      glow: 'from-purple-400/40 via-indigo-400/10 to-transparent',
    },
    {
      icon: Users,
      label: t.welcomeFeatureCommunityLabel,
      detail: t.welcomeFeatureCommunityDetail,
      glow: 'from-sky-400/40 via-emerald-400/10 to-transparent',
    },
    {
      icon: Trophy,
      label: t.welcomeFeatureRewardsLabel,
      detail: t.welcomeFeatureRewardsDetail,
      glow: 'from-rose-400/40 via-pink-400/10 to-transparent',
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 backdrop-blur-md px-4">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.25),_transparent_65%)]" />
      <div
        className={`relative w-full max-w-3xl overflow-hidden rounded-3xl border border-white/40 bg-white/90 p-8 shadow-2xl transition-all duration-300 backdrop-blur-xl ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
      >
        <div
          className="absolute -top-10 -right-6 h-40 w-40 rounded-full bg-gradient-to-br from-orange-300/60 via-rose-300/30 to-transparent blur-3xl animate-pulse"
          aria-hidden
        />
        <div
          className="absolute bottom-0 left-0 h-48 w-48 rounded-full bg-gradient-to-tr from-sky-200/60 via-emerald-200/30 to-transparent blur-3xl animate-[spin_18s_linear_infinite]"
          role="img"
          aria-label={t.welcomeAnimationAlt}
        />

        <button
          type="button"
          onClick={finish}
          aria-label={t.welcomeSkip}
          className="absolute top-4 right-4 text-slate-400 transition hover:text-slate-600"
        >
          <X size={24} />
        </button>

        <div className="relative space-y-6 text-center">
          <span className="inline-flex items-center justify-center rounded-full bg-amber-100/80 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-amber-700">
            {t.welcomeSpotlightTag}
          </span>
          <h2 className="text-3xl font-black leading-tight text-slate-900 sm:text-4xl">
            {t.welcomeTitle}
          </h2>
          <p className="mx-auto max-w-2xl text-base text-slate-600 sm:text-lg">
            {t.welcomeSubtitle}
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {features.map(({ icon: Icon, label, detail, glow }) => (
            <div
              key={label}
              className="group relative overflow-hidden rounded-2xl border border-white/70 bg-white/70 p-4 shadow-lg backdrop-blur-sm"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${glow} opacity-0 transition duration-300 group-hover:opacity-100`} />
              <div className={`relative flex gap-4 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <span className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-white/90 text-amber-500 shadow-inner shadow-amber-100 ${isRTL ? 'order-2' : ''}`}>
                  <Icon size={26} />
                </span>
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <p className="font-semibold text-slate-900">{label}</p>
                  <p className="text-sm leading-snug text-slate-600">{detail}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={`mt-10 flex flex-col gap-3 sm:flex-row ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
          <button
            type="button"
            onClick={finish}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-emerald-200 transition hover:from-emerald-600 hover:to-emerald-700 hover:shadow-xl"
          >
            {t.welcomeCTA}
            <ArrowRight size={18} className={isRTL ? 'rotate-180' : ''} />
          </button>
          <button
            type="button"
            onClick={finish}
            className="inline-flex items-center justify-center rounded-2xl border border-slate-200 px-6 py-3 text-base font-semibold text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
          >
            {t.welcomeSkip}
          </button>
        </div>
      </div>
    </div>
  );
};
